// L'EFFACEUR - Assistant du Bug (Chatbot Component)
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, Sparkles,
  HelpCircle, FileText, Clock, DollarSign, AlertCircle, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';

// Quick answer icons mapping
const QUICK_ICONS = {
  status: FileText,
  documents: FileText,
  delays: Clock,
  fees: DollarSign,
  help: HelpCircle
};

const AssistantDuBug = ({ isOpen, onClose, isFloating = true }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [quickAnswers, setQuickAnswers] = useState([]);
  const [missionStatus, setMissionStatus] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load quick answers on mount
  useEffect(() => {
    const loadQuickAnswers = async () => {
      try {
        const res = await axios.get(`${API}/chatbot/quick-answers`);
        setQuickAnswers(res.data.quick_answers || []);
      } catch (error) {
        console.error('Error loading quick answers:', error);
      }
    };
    loadQuickAnswers();
  }, []);
  
  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Bonjour ! Je suis **L'Assistant du Bug** 🐛, votre expert protecteur chez L'EFFACEUR.

Je suis là pour vous aider avec :
• Le suivi de votre dossier
• Les questions sur les documents requis
• Les délais de résiliation
• Toute autre question sur nos services

Comment puis-je vous aider aujourd'hui ?`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen, messages.length]);
  
  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;
    
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ message: messageText });
      if (conversationId) params.append('conversation_id', conversationId);
      
      const res = await axios.post(
        `${API}/chatbot/message?${params.toString()}`,
        {},
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true 
        }
      );
      
      setConversationId(res.data.conversation_id);
      if (res.data.mission_status) {
        setMissionStatus(res.data.mission_status);
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      }]);
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Erreur de communication avec l\'assistant');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Désolé, j\'ai rencontré une erreur. Veuillez réessayer.',
        timestamp: new Date().toISOString(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const clearHistory = async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/chatbot/history/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      setMessages([]);
      setConversationId(null);
      toast.success('Historique effacé');
    } catch (error) {
      toast.error('Erreur lors de l\'effacement');
    }
  };
  
  // Format markdown-like text
  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>')
      .replace(/•/g, '&#8226;');
  };
  
  const chatContent = (
    <div className={`flex flex-col ${isFloating ? 'h-[500px]' : 'h-full'} bg-gray-900`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gradient-to-r from-cyan-400/10 to-magenta-400/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-magenta-400 flex items-center justify-center">
            <Bot className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="font-orbitron font-bold text-white text-sm">L'Assistant du Bug</h3>
            <p className="text-xs text-gray-400">Expert protecteur</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearHistory}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Nouvelle conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          {isFloating && (
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      
      {/* Mission Status Badge */}
      {missionStatus && (
        <div className="px-4 py-2 bg-gray-800/50 border-b border-gray-800">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-400">Votre mission :</span>
            <span className={`px-2 py-0.5 rounded-full ${
              missionStatus.progress >= 80 ? 'bg-green-400/20 text-green-400' :
              missionStatus.progress >= 40 ? 'bg-cyan-400/20 text-cyan-400' :
              'bg-orange-400/20 text-orange-400'
            }`}>
              {missionStatus.label} ({missionStatus.progress}%)
            </span>
          </div>
        </div>
      )}
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.role === 'user' 
                  ? 'bg-cyan-400/20' 
                  : msg.isError ? 'bg-red-400/20' : 'bg-magenta-400/20'
              }`}>
                {msg.role === 'user' 
                  ? <User className="w-4 h-4 text-cyan-400" />
                  : <Bot className={`w-4 h-4 ${msg.isError ? 'text-red-400' : 'text-magenta-400'}`} />
                }
              </div>
              <div className={`rounded-2xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'bg-cyan-400/20 text-white rounded-tr-sm'
                  : msg.isError ? 'bg-red-400/10 text-red-300 rounded-tl-sm' : 'bg-gray-800 text-gray-200 rounded-tl-sm'
              }`}>
                <div 
                  className="text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                />
              </div>
            </div>
          </motion.div>
        ))}
        
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-magenta-400/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-magenta-400" />
              </div>
              <div className="bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                  <span className="text-sm text-gray-400">L'Assistant réfléchit...</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Answers */}
      {messages.length <= 1 && quickAnswers.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-800">
          <p className="text-xs text-gray-500 mb-2">Questions fréquentes :</p>
          <div className="flex flex-wrap gap-2">
            {quickAnswers.map((qa) => {
              const IconComp = QUICK_ICONS[qa.id] || HelpCircle;
              return (
                <button
                  key={qa.id}
                  onClick={() => sendMessage(qa.text)}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded-full transition-colors disabled:opacity-50"
                >
                  <span>{qa.icon}</span>
                  <span>{qa.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Input */}
      <div className="p-4 border-t border-gray-800">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Posez votre question..."
            disabled={loading}
            className="flex-1 bg-gray-800 border border-gray-700 rounded-full px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 disabled:opacity-50"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-400 to-magenta-400 hover:opacity-90 disabled:opacity-50 p-0"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-gray-600 text-center mt-2">
          Powered by Gemini 2.0 Flash • <span className="text-cyan-400">L'EFFACEUR</span>
        </p>
      </div>
    </div>
  );
  
  // Floating button + modal version
  if (isFloating) {
    return (
      <>
        {/* Floating Button */}
        <AnimatePresence>
          {!isOpen && (
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              onClick={() => onClose()} // Toggle open
              className="fixed bottom-20 right-4 md:right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-cyan-400 to-magenta-400 shadow-lg hover:shadow-cyan-400/30 transition-all flex items-center justify-center group"
              data-testid="chatbot-toggle-btn"
            >
              <Bot className="w-6 h-6 text-black group-hover:scale-110 transition-transform" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full animate-pulse" />
              {/* Badge "IA 24/7" sur mobile */}
              <span className="absolute -top-8 right-0 text-[10px] bg-success/90 text-black px-2 py-0.5 rounded-full font-bold whitespace-nowrap md:hidden">IA 24/7</span>
            </motion.button>
          )}
        </AnimatePresence>
        
        {/* Chat Modal */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="fixed bottom-24 right-4 left-4 md:left-auto md:right-6 z-50 md:w-[380px] max-h-[70vh] rounded-2xl overflow-hidden shadow-2xl shadow-cyan-400/10 border border-gray-800"
              data-testid="chatbot-modal"
            >
              {chatContent}
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
  
  // Embedded version (for sidebar tab)
  return (
    <div className="h-full rounded-xl overflow-hidden border border-gray-800" data-testid="chatbot-embedded">
      {chatContent}
    </div>
  );
};

export default AssistantDuBug;
